import { useCallback, useEffect, useState } from 'react';
import { useInjectReducer } from 'utils/redux-injectors';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  selectAchLinkError,
  selectIsNewPasswordDialogVisible,
  selectUserProfile,
} from 'app/features/User/selectors';
import { actions, sliceKey, reducer } from './slice';
import { useUserSlice } from '../User';
import PDPDrawer from 'app/components/PDP/lodable';
import { useProfileSlice } from '../Profile';
import { usePaymentsSlice } from '../Payments/slice/index';
import { useMentorPlus } from '../MentorPlus/hooks';
import { lazyLoad } from 'utils/loadable';
import { useRealtimePrice } from '../RealtimePrice';
import { selectAuthToken } from './selectors';
import { getLocalStorageItem } from 'utils/functions';

const SupportMagnifiLink = lazyLoad(
  () =>
    import(/* webpackChunkName: 'SupportMagnifiLink' */ './SupportMagnifiLink'),
  m => m.SupportMagnifiLink,
);

const ResetPasswordDialog = lazyLoad(
  () =>
    import(
      /* webpackChunkName: 'ResetPasswordDialog' */ './ResetPasswordDialog'
    ),
  m => m.ResetPasswordDialog,
);

const LoggedOutAuthState = lazyLoad(
  () => import(/* webpackChunkName: 'AuthLoggedOutState' */ './LoggedOut'),
  m => m.default,
);

const LoggedInAuthState = lazyLoad(
  () => import(/* webpackChunkName: 'AuthLoggedInState' */ './LoggedIn'),
  m => m.default,
);

const AlertDialog = lazyLoad(
  () =>
    import(/* webpackChunkName: 'AlertDialog' */ 'app/components/AlertDialog'),
  m => m.default,
);

const INFO_REDIRECT_URL =
  process.env.REACT_APP_INFO_REDIRECT ?? 'https://invest.magnifi.com';
interface Props {
  modalValue: string;
  toggleModalValue: (val: string) => void;
}

export default function Auth(props: Props) {
  const [passVisible, setNewPassVisible] = useState(false);
  const isReEnrollPasswordDialogVisible = useSelector(
    selectIsNewPasswordDialogVisible,
  );
  useMentorPlus();
  useProfileSlice();
  usePaymentsSlice();
  useInjectReducer({ key: sliceKey, reducer });
  useUserSlice();
  const { actions: rtActions } = useRealtimePrice();
  const achLinkErrorMessage = useSelector(selectAchLinkError);
  const [achLinkErrorModal, toggleAchLinkErrorModal] = useState(false);
  const { search: locationSearchValue, pathname } = useLocation();
  const dispatch = useDispatch();

  const initialise = useCallback(() => {
    dispatch(rtActions.initialiseRealtimePrice());
  }, [rtActions, dispatch]);
  const localToken = getLocalStorageItem('authToken');
  const authToken = useSelector(selectAuthToken);
  const email = useSelector(selectUserProfile)?.email;

  useEffect(() => {
    initialise();
  }, [initialise]);

  useEffect(() => {
    const authenticatedRoutes = ['/watchlist', '/settings', '/accounts'];
    if (pathname.length > 0) {
      dispatch(
        actions.updateRedirectRoute(
          authenticatedRoutes.some(path => pathname.includes(path))
            ? pathname
            : undefined,
        ),
      );
    }
  }, [dispatch, pathname]);

  const redirectSponsorLogos = useCallback(
    (oldUrl: string) => {
      if (pathname.includes(oldUrl)) {
        window.location.href = pathname.replaceAll(
          oldUrl,
          'https://static.magnifi.com/sponsors/',
        );
      }
    },
    [pathname],
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    // Check for redirection of Static Content Websites Start
    redirectSponsorLogos('https://magnifi.com/assets/images/sponsors/');
    redirectSponsorLogos('https://www.magnifi.com/assets/images/sponsors/');

    if (pathname.includes('pages')) {
      const ACTUAL_REDIRECT_ROUTE = pathname.split('pages');
      window.location.href = `${INFO_REDIRECT_URL}${
        ACTUAL_REDIRECT_ROUTE[ACTUAL_REDIRECT_ROUTE.length - 1]
      }${locationSearchValue || ''}`;
      // Check for redirection of Static Content Websites End
    }
  }, [locationSearchValue, pathname, redirectSponsorLogos]);

  //USE EFFECT TO HANDLE LINK TRIGGERING DIFFERENT DRAWERS

  return (
    <>
      {isReEnrollPasswordDialogVisible && (
        <ResetPasswordDialog
          passVisible={passVisible}
          setNewPassVisible={setNewPassVisible}
        />
      )}
      <PDPDrawer />
      {achLinkErrorModal && (
        <AlertDialog
          show={achLinkErrorModal}
          description={
            achLinkErrorMessage?.includes('support@magnifi.com.') ? (
              <SupportMagnifiLink email={email} />
            ) : (
              achLinkErrorMessage
            )
          }
          toggleModal={() => toggleAchLinkErrorModal(!achLinkErrorModal)}
          cancelButtonText={'Ok'}
        />
      )}
      {!(authToken || localToken) ? (
        <LoggedOutAuthState props={props} />
      ) : (
        <LoggedInAuthState toggleAchLinkErrorModal={toggleAchLinkErrorModal} />
      )}
    </>
  );
}
